import React, { useRef, useState, useEffect, useContext } from 'react';
import { X, CheckCircle2, Copy, Ticket, Loader2 } from 'lucide-react';
import API_BASE_URL from '../../config';
import { getToken } from '../../utils/tokenUtils';
import { WalletContext } from '../../context/WalletContext';

const RewardModal = ({ reward, onClose, onRefresh }) => {
  const canvasRef = useRef(null);
  const confettiCanvasRef = useRef(null);
  const { refreshWallet } = useContext(WalletContext);
  const [scratching, setScratching] = useState(false);
  const [scratchedPercent, setScratchedPercent] = useState(0);
  const [isScratched, setIsScratched] = useState(reward.isScratched);
  const [isClaimed, setIsClaimed] = useState(reward.isClaimed);
  const [claimLoading, setClaimLoading] = useState(false);
  const [scratchLoading, setScratchLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Resolved reward state (received after scratching)
  const [resolved, setResolved] = useState({
    rewardType: reward.resolvedRewardType || '',
    rewardValue: reward.rewardValue || '',
    displayTitle: reward.resolvedRewardType === 'Coins' ? 'FitScan Coins' : 'Promo Code Discount',
    displayDescription: reward.resolvedRewardType === 'Coins' 
      ? `You won ${reward.rewardValue} FitScan Coins!` 
      : `Use promo code ${reward.rewardValue} for your reward.`
  });

  // Sync with prop updates
  useEffect(() => {
    if (reward.isScratched) {
      setIsScratched(true);
    }
    if (reward.isClaimed) {
      setIsClaimed(true);
    }
    if (reward.resolvedRewardType || reward.rewardValue) {
      setResolved({
        rewardType: reward.resolvedRewardType || '',
        rewardValue: reward.rewardValue || '',
        displayTitle: reward.resolvedRewardType === 'Coins' ? 'FitScan Coins' : 'Promo Code Discount',
        displayDescription: reward.resolvedRewardType === 'Coins' 
          ? `You won ${reward.rewardValue} FitScan Coins!` 
          : `Use promo code ${reward.rewardValue} for your reward.`
      });
    }
  }, [reward]);

  // Confetti Particle Celebration Effect
  useEffect(() => {
    console.log('[Confetti] Effect triggered. showConfetti:', showConfetti, 'canvas:', confettiCanvasRef.current);
    if (!showConfetti || !confettiCanvasRef.current) return;

    const canvas = confettiCanvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      console.log('[Confetti] Canvas resized to:', canvas.width, 'x', canvas.height);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const colors = ['#6CD800', '#3b82f6', '#f59e0b', '#ec4899', '#10b981', '#a855f7', '#38bdf8', '#fb7185'];
    const particles = [];
    const particleCount = 140;

    // Explode from the middle of the screen
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const velocity = Math.random() * 9 + 4;
      particles.push({
        x: centerX,
        y: centerY - 60,
        vx: Math.cos(angle) * velocity + (Math.random() * 2 - 1),
        vy: Math.sin(angle) * velocity - (Math.random() * 4 + 2), // upward bias
        r: Math.random() * 5 + 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        tiltAngle: Math.random() * Math.PI,
        tiltAngleIncremental: Math.random() * 0.12 + 0.04,
        gravity: 0.16,
        friction: 0.985,
        opacity: 1,
      });
    }
    console.log('[Confetti] Particles generated:', particles.length);

    let startTime = Date.now();
    const duration = 3500; // 3.5 seconds

    const draw = () => {
      const elapsed = Date.now() - startTime;
      if (elapsed > duration) {
        console.log('[Confetti] Animation duration elapsed. Resetting.');
        setShowConfetti(false);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      let activeParticles = 0;
      particles.forEach((p, idx) => {
        p.vx *= p.friction;
        p.vy *= p.friction;
        p.vy += p.gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.tiltAngle += p.tiltAngleIncremental;

        if (elapsed > duration - 1000) {
          p.opacity = Math.max(0, 1 - (elapsed - (duration - 1000)) / 1000);
        }

        const cond1 = p.opacity > 0;
        const cond2 = p.y < canvas.height;
        const cond3 = p.x > 0;
        const cond4 = p.x < canvas.width;

        if (idx === 0) {
          console.log('[Confetti] Particle 0 check:', {
            x: p.x,
            y: p.y,
            opacity: p.opacity,
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
            cond1, cond2, cond3, cond4
          });
        }

        if (cond1 && cond2 && cond3 && cond4) {
          activeParticles++;
          ctx.save();
          ctx.globalAlpha = p.opacity;
          ctx.translate(p.x, p.y);
          ctx.rotate(p.tiltAngle);
          ctx.fillStyle = p.color;

          const width = p.r * 1.5;
          const height = p.r * 0.75;
          ctx.fillRect(-width / 2, -height / 2, width, height);
          ctx.restore();
        }
      });

      if (activeParticles > 0) {
        animationFrameId = requestAnimationFrame(draw);
      } else {
        console.log('[Confetti] No active particles left on frame. Resetting. activeCount:', activeParticles);
        setShowConfetti(false);
      }
    };

    draw();

    return () => {
      console.log('[Confetti] Cleanup run.');
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [showConfetti]);

  const canvasWidth = 320;
  const canvasHeight = 150;

  // Initialize Canvas
  useEffect(() => {
    if (isScratched || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    // Set display size
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Create a beautiful metallic silver background
    const grad = ctx.createLinearGradient(0, 0, canvasWidth, canvasHeight);
    grad.addColorStop(0, '#E2E8F0'); // Slate 200
    grad.addColorStop(0.2, '#94A3B8'); // Slate 400
    grad.addColorStop(0.5, '#CBD5E1'); // Slate 300
    grad.addColorStop(0.8, '#64748B'); // Slate 500
    grad.addColorStop(1, '#94A3B8'); // Slate 400
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw some noise/sparkles to look premium
    ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
    for (let i = 0; i < 120; i++) {
      ctx.fillRect(
        Math.random() * canvasWidth,
        Math.random() * canvasHeight,
        1.5,
        1.5
      );
    }

    // Add overlay text
    ctx.fillStyle = '#0B0F19';
    ctx.font = '900 20px "Inter", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH TO REVEAL', canvasWidth / 2, canvasHeight / 2 - 10);

    ctx.fillStyle = '#334155';
    ctx.font = 'bold 11px "Inter", sans-serif';
    ctx.fillText('SCRATCH OVER 55% OF THE CARD', canvasWidth / 2, canvasHeight / 2 + 18);
  }, [isScratched]);

  // Scratch Drawing Helpers
  const getPointerPos = (e) => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const rect = canvasRef.current.getBoundingClientRect();
    
    // Support mouse or touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    return {
      x: ((clientX - rect.left) / rect.width) * canvasWidth,
      y: ((clientY - rect.top) / rect.height) * canvasHeight
    };
  };

  const drawScratch = (x, y) => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 22, 0, Math.PI * 2);
    ctx.fill();
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    setScratching(true);
    const pos = getPointerPos(e);
    drawScratch(pos.x, pos.y);
  };

  const handlePointerMove = (e) => {
    if (!scratching) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    drawScratch(pos.x, pos.y);
  };

  const checkScratchPercentage = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const imgData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);
    const pixels = imgData.data;
    let transparentCount = 0;

    // Sample every 4th pixel for speed
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] === 0) {
        transparentCount++;
      }
    }

    const totalSampled = pixels.length / 4;
    const percent = (transparentCount / totalSampled) * 100;
    setScratchedPercent(percent);

    if (percent >= 55) {
      triggerScratchReveal();
    }
  };

  const handlePointerUp = () => {
    setScratching(false);
    checkScratchPercentage();
  };

  const triggerScratchReveal = async () => {
    if (isScratched || scratchLoading) return;
    setScratchLoading(true);
    try {
      const rawToken = getToken();
      const response = await fetch(`${API_BASE_URL}/api/member/rewards/${reward.id}/scratch`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${rawToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[RewardModal] Scratch response successful:', data);
        setResolved({
          rewardType: data.rewardType,
          rewardValue: data.rewardValue,
          displayTitle: data.displayTitle,
          displayDescription: data.displayDescription
        });
        setIsScratched(true);
        console.log('[RewardModal] Setting showConfetti to true');
        setShowConfetti(true);
        refreshWallet();
        onRefresh();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setScratchLoading(false);
    }
  };

  const handleClaim = async () => {
    if (claimLoading || isClaimed) return;
    setClaimLoading(true);
    try {
      const rawToken = getToken();
      const response = await fetch(`${API_BASE_URL}/api/member/rewards/${reward.id}/claim`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${rawToken}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setIsClaimed(true);
        refreshWallet();
        onRefresh();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setClaimLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(resolved.rewardValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      {/* Confetti Celebration Canvas Overlay */}
      {showConfetti && (
        <canvas
          ref={confettiCanvasRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 99999,
          }}
        />
      )}
      <div className="w-full max-w-md bg-[#0F172A] border border-[#1E293B] rounded-2xl overflow-hidden shadow-2xl relative">
        
        {/* Close Button */}
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 text-gray-400 hover:text-white p-1.5 rounded-full hover:bg-gray-800/50 transition-colors z-10"
        >
          <X size={18} />
        </button>

        {/* Modal Body */}
        <div className="p-6 flex flex-col items-center text-center">
          
          <div className="h-14 w-14 rounded-full bg-[#6CD800]/10 border border-[#6CD800]/20 flex items-center justify-center mb-4 text-[#6CD800]">
            <Ticket size={28} />
          </div>

          <h3 className="text-xl font-black text-white tracking-wide font-bebas uppercase">
            Consistency Milestone Card
          </h3>
          <p className="text-xs text-gray-400 mt-1">
            Earned for completing {reward.milestoneDay} attendance scans
          </p>

          {/* Interactive Scratch Area */}
          <div className="relative w-full h-40 mt-6 rounded-xl overflow-hidden bg-[#0B0F19] border border-gray-800 flex flex-col items-center justify-center select-none">
            
            {/* The Hidden Content Underneath */}
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center z-0">
              {scratchLoading ? (
                <Loader2 className="animate-spin text-[#6CD800]" size={28} />
              ) : (
                <div className="animate-scale-up space-y-2">
                  {resolved.rewardType === 'Coins' ? (
                    <div className="flex flex-col items-center">
                      <div className="text-3xl font-black text-[#6CD800] tracking-wide flex items-center gap-1.5">
                        🪙 {resolved.rewardValue}
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">FitScan Coins</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className="text-xl font-extrabold text-lime-400 select-all font-mono bg-[#1E293B] px-3 py-1.5 rounded-lg border border-gray-700 max-w-[240px] truncate">
                        {resolved.rewardValue}
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mt-1">Promo Code Reward</span>
                    </div>
                  )}
                  <h4 className="text-xs font-bold text-gray-300 mt-1">{resolved.displayTitle}</h4>
                  <p className="text-[10px] text-gray-400 max-w-[280px] leading-relaxed">
                    {resolved.displayDescription}
                  </p>
                </div>
              )}
            </div>

            {/* Silver Scratch Layer Canvas overlay */}
            {!isScratched && (
              <canvas
                ref={canvasRef}
                onMouseDown={handlePointerDown}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerUp}
                onMouseLeave={handlePointerUp}
                onTouchStart={handlePointerDown}
                onTouchMove={handlePointerMove}
                onTouchEnd={handlePointerUp}
                className="absolute inset-0 cursor-crosshair z-10 w-full h-full touch-none"
              />
            )}
          </div>

          {/* Post Scratch Actions / Claiming */}
          {isScratched && (
            <div className="w-full mt-6 space-y-3">
              {resolved.rewardType === 'PromoCode' && (
                <button
                  onClick={copyToClipboard}
                  className="w-full py-2 bg-[#1E293B] hover:bg-[#25324d] text-gray-300 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-colors border border-gray-800"
                >
                  <Copy size={13} /> {copied ? 'Copied!' : 'Copy Code'}
                </button>
              )}

              {isClaimed ? (
                <div className="flex items-center justify-center gap-2 p-2.5 bg-[#6CD800]/10 border border-[#6CD800]/20 text-[#6CD800] rounded-lg text-sm font-bold w-full">
                  <CheckCircle2 size={16} /> Claimed Successfully
                </div>
              ) : (
                <button
                  onClick={handleClaim}
                  disabled={claimLoading}
                  className="w-full py-3 bg-[#6CD800] text-[#0B0F19] rounded-lg text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#5bc200] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {claimLoading && <Loader2 className="animate-spin" size={16} />}
                  Claim Reward
                </button>
              )}
            </div>
          )}

          {/* Prompt to scratch */}
          {!isScratched && (
            <div className="mt-4 text-[10px] font-bold tracking-wider text-gray-500 uppercase">
              {scratchedPercent > 0 
                ? `Scratching... ${Math.round(scratchedPercent)}% cleared` 
                : 'Rub card to reveal reward details'
              }
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default RewardModal;
